import React from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  Divider as MuiDivider,
  Link,
  Paper,
  Typography as MuiTypography,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import MaterialTable from "material-table";
import CopyIcon from "@material-ui/icons/FileCopy";
import { ExpandMore } from "@material-ui/icons";

import { NavLink } from "react-router-dom";
import { useApp } from "../../../../AppProvider";

import { copyToClipboard, dateFormatter } from "../../../../utils";
import BasicPermitGroupInfo from "./BasicPermitGroupInfo";
import { usePermitGroups } from "./usePermitGroups";
import LinkedPermitsManagement from "./LinkedPermitsManagement";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Typography = styled(MuiTypography)(spacing);

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)}px);
  height: 100%;
  width: 100%;
`;
function PermitGroupsManagement() {
  const {
    selectedPermitGroup,
    setSelectedPermitGroup,
    data,
    isFetching,
    error,
    refetch,
    PERMIT_GROUPS_TABLE_COLUMNS,
  } = usePermitGroups();
  const { doToast } = useApp();

  const [triggerRefetch, setTriggerRefetch] = React.useState(false);
  const handleTriggerRefetch = () => setTriggerRefetch(!triggerRefetch);
  const handleRefetch = () => {
    refetch().then(() => {
      handleTriggerRefetch();
    });
  };

  // const handleDelete = (oldData) => {
  //   return (async () => {
  //     try {
  //       if (oldData) {
  //         const token = await getAccessTokenSilently();
  //         const headers = { Authorization: `Bearer ${token}` };
  //         await axios.delete(
  //           `${process.env.REACT_APP_ENDPOINT}/api/list-permit-groups/${oldData.permit_group_ndx}`,
  //           { headers }
  //         );
  //         doToast("success", "This entry was deleted from the database");
  //         if (selectedPermitGroup === oldData.permit_group_ndx) {
  //           setSelectedPermitGroup(null);
  //         }
  //
  //         await refetch();
  //       } else {
  //         doToast("error", "Something went wrong");
  //       }
  //     } catch (err) {
  //       console.error(err);
  //       const message = err?.message ?? "Something went wrong";
  //       doToast("error", message);
  //     }
  //   })();
  // };

  if (error) return "An error has occurred: " + error.message;
  return (
    <React.Fragment>
      <Helmet title="Permit Groups Management" />
      <Typography variant="h3" gutterBottom display="inline">
        Permit Groups Management
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} exact to="/dashboard">
          Dashboard
        </Link>
        <Typography>Permit Groups Management</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <Box>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box style={{ width: "100%" }}>
              <Typography variant="h3">Permit Groups Selection</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableWrapper>
              <MaterialTable
                id="Permit Groups Management"
                title={`Permit Groups Management ${dateFormatter(
                  new Date(),
                  "MM/DD/YYYY, h:mm A"
                )}`}
                loading={isFetching || !data}
                columns={PERMIT_GROUPS_TABLE_COLUMNS}
                data={data}
                localization={{
                  toolbar: { searchPlaceholder: "Permit Groups Selection" },
                }}
                components={{
                  Container: (props) => <div {...props} />,
                }}
                // editable={{
                //   onRowDelete: handleDelete,
                // }}
                actions={[
                  {
                    icon: CopyIcon,
                    tooltip: "Copy Data",
                    isFreeAction: true,
                    onClick: () => {
                      try {
                        copyToClipboard(data, PERMIT_GROUPS_TABLE_COLUMNS, () =>
                          doToast(
                            "success",
                            "Data was copied to your clipboard."
                          )
                        );
                      } catch (error) {
                        const message =
                          error?.message ?? "Something went wrong";
                        doToast("error", message);
                      }
                    },
                  },
                  () => ({
                    icon: "edit",
                    tooltip: "Edit",
                    onClick: (event, rowData) => {
                      setSelectedPermitGroup(rowData?.permit_group_ndx);
                    },
                  }),
                  {
                    icon: "add_box",
                    tooltip: "Add",
                    isFreeAction: true,
                    onClick: () => {
                      setSelectedPermitGroup(0);
                    },
                  },
                ]}
                options={{
                  filtering: true,
                  emptyRowsWhenPaging: false,
                  showTitle: false,
                  columnsButton: true,
                  exportButton: true,
                  exportAllData: true,
                  addRowPosition: "first",
                  pageSize: 10,
                  pageSizeOptions: [10, 30, 50, 100, 200],
                  padding: "dense",
                  searchFieldAlignment: "left",
                  minBodyHeight: "250px",
                  maxBodyHeight:
                    "calc(100vh - 64px - 48px - 106px - 48px - 64px - 204px - 100px)",
                }}
              />
            </TableWrapper>
          </AccordionDetails>
        </Accordion>

        <Box
          style={{
            display:
              selectedPermitGroup || selectedPermitGroup === 0
                ? "block"
                : "none",
          }}
        >
          <Paper style={{ margin: "16px 0" }}>
            <Box
              p={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h4">
                {selectedPermitGroup === 0
                  ? "Creating New Permit Group"
                  : "Editing Existing Permit Group"}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setSelectedPermitGroup(null)}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
          <BasicPermitGroupInfo
            selectedPermitGroup={selectedPermitGroup}
            setSelectedPermitGroup={setSelectedPermitGroup}
            refetchPermitGroups={handleRefetch}
            triggerRefetch={triggerRefetch}
          />
          <LinkedPermitsManagement
            selectedPermitGroup={selectedPermitGroup}
            refetchPermitGroups={handleRefetch}
          />
          <Paper style={{ margin: "16px 0" }}>
            <Box
              p={4}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h4">
                {selectedPermitGroup === 0
                  ? "Creating New Permit Group"
                  : "Editing Existing Permit Group"}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setSelectedPermitGroup(null)}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </React.Fragment>
  );
}

export default PermitGroupsManagement;
